import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { getEffectiveDatesOptions } from 'global/services-helpers/domainService';
import { makeAutoObservable } from 'mobx';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import {
  sunLifeGroupSizeValidation,
  sunLifeSICAdditionalValidations,
  sunLifeSICWarningMessage,
} from 'global/utils/sponsor-validations/sunLife/sicValidation';
import {
  INCORPORATION_DATE_VALIDATION_MSG,
  isIncorporationDateValid,
} from 'global/utils/sponsor-validations/sunLife/incorporationDateValidation';
import { ReactNode } from 'react';

type SunLifeQuoteProposalInfoFields = {
  proposalName: FormControl<string>;
  effectiveDate: FormControl<string>;
  incorporationDate: FormControl<string>;
  groupSize: FormControl<string>;
  zip: FormControl<string>;
  state: FormControl<string>;
  county: FormControl<string>;
  sic4: FormControl<string>;
};

export class SunLifeQuoteProposalInfoFormDelegate {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  public proposalForm: FormController<SunLifeQuoteProposalInfoFields>;

  public get effectiveDatesOptions(): OptionType[] {
    return getEffectiveDatesOptions(this._rootStore.domainStoreV2.effectiveDates);
  }

  /** Shown as a warning `Alert` when the current SIC matches a non-blocking rule. */
  public get sicWarningMessage(): ReactNode | null {
    const sicValue = this.proposalForm.fields.sic4.value;
    return sicValue && sicValue.length === 4 ? sunLifeSICWarningMessage(sicValue) : null;
  }

  public get showIncorporationDate(): boolean {
    return !!this.proposalForm.fields.incorporationDate.value;
  }

  constructor() {
    this.proposalForm = FormController.create(
      {
        proposalName: new FormControl<string>('', {
          validations: ['printableCharacters'],
        }),
        effectiveDate: new FormControl<string>(''),
        groupSize: new FormControl<string>('', {
          customValidations: [sunLifeGroupSizeValidation],
        }),
        incorporationDate: new FormControl<string>('', {
          optional: true,
          enabledWithFields: ['effectiveDate'],
          customValidations: [
            {
              check: (incorporationDate: string): boolean => {
                return isIncorporationDateValid(incorporationDate, this.proposalForm.fields.effectiveDate.value);
              },
              msg: INCORPORATION_DATE_VALIDATION_MSG,
            },
          ],
        }),
      },
      {
        capabilities: [
          {
            type: 'zip',
            params: {
              zip: { optional: false },
              state: {
                customValidations: [
                  {
                    check: this._rootStore.domainStoreV2.isEligibleState,
                    msg: 'State is ineligible',
                  },
                ],
              },
            },
          },
          {
            type: 'sic',
            params: {
              additionalValidations: sunLifeSICAdditionalValidations,
            },
          },
        ],
      },
    );

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
